import React, { useEffect, useState, useMemo, useRef } from 'react';
import styles from './HistoricalHolderCount.module.sass';
import { AxisOptions, Chart } from 'react-charts';
import cn from 'classnames';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TablePagination from 'components/TablePagination';
import HolderCountChart from 'components/Modal/HolderCountChart';
import Spinner from 'components/Spinner';
import { Activity } from 'lucide-react';

type MyDatum = { Date: string; Holder_Count: number; timestamp?: number };

interface ModalData {
  data: MyDatum[];
  trendData: {
    isPositive: boolean;
    rateOfChange: number;
    firstValue: number;
    lastValue: number;
  };
}

interface HistoricalHolderCountProps {
  selectedIndex: number;
  onSelectionChange: (index: number, contract: string, chain: string, token: string) => void;
  modalData: ModalData | null;
  setModalData: (data: ModalData | null) => void;
  page: number;
  setPage: (page: number) => void;
  rowsPerPage: number;
  cachedData: any;
  detailedData?: any;
  selectedToken?: string;
}

// function HistoricalHolderCount({
const HistoricalHolderCount = React.memo(function HistoricalHolderCount({
  selectedIndex,
  onSelectionChange,
  modalData,
  setModalData,
  page,
  setPage,
  rowsPerPage,
  cachedData,
  detailedData,
  selectedToken
}: HistoricalHolderCountProps) {
  // Track initial mount
  const didInitialMount = useRef(false);
  
  // State to track loading state
  const [isLoading, setIsLoading] = useState(false);

  const dataDuration = useSelector((state: RootState) => state.user.dataDuration);
  const loading = useSelector((state: RootState) => state.portfolio.loading);
  const historicHolderData = useSelector(
    (state: RootState) => state.portfolio.historicHolder
  );
  
  // Use provided cachedData if available, otherwise use Redux data
  const dataToUse = cachedData || historicHolderData;

  // Update loading state when selection changes
  useEffect(() => {
    if (selectedToken) {
      // Loading is set to true in the handleItemClick function
      // Only clear loading once data is available
      if (detailedData?.result) {
        const timer = setTimeout(() => {
          setIsLoading(false);
        }, 300); // Short delay to ensure data is processed
        
        return () => clearTimeout(timer);
      }
    }
  }, [selectedToken, detailedData]);

  // Transform data with memoization
  const { paginatedData, holderData, contractData } = useMemo(() => {
    if (!dataToUse) {
      return { paginatedData: [], holderData: {}, contractData: {} };
    }

    const { result, contract } = transformData(dataToUse);
    const sortedKeys = Object.keys(result).sort(
      (a, b) => result[b][0]?.Holder_Count - result[a][0]?.Holder_Count
    );
    const paginatedKeys = sortedKeys.slice(
      (page - 1) * rowsPerPage,
      page * rowsPerPage
    );

    return {
      paginatedData: paginatedKeys,
      holderData: result,
      contractData: contract,
    };
  }, [dataToUse, page, rowsPerPage, dataDuration]);

  // Effect for page changes only - explicitly tied to page changes
  useEffect(() => {
    // Skip on first render
    if (!didInitialMount.current) {
      didInitialMount.current = true;
      return;
    }

    // Only change selection if the page changes and we have data
    if (paginatedData.length > 0 && page > 0) {
      // Select first item on the new page and notify parent component
      const [token, chain] = paginatedData[0].split('-');
      if (token && chain && dataToUse?.[chain]?.[token]) {
        const tokenData = dataToUse[chain][token];
        // Add a flag to indicate this is from a page change
        onSelectionChange(0, tokenData.contract, chain, token);
      }
    }
    // Only depend on page changes, not data or selection changes
  }, [page]);

  function transformData(data: {
    [x: string]: any;
    hasOwnProperty: (arg0: string) => any;
  }) {
    const result: { [key: string]: any[] } = {};
    const contract: { [key: string]: any } = {};
    
    for (const network in data) {
      if (data.hasOwnProperty(network)) {
        const assets = data[network];
        for (const asset in assets) {
          if (assets.hasOwnProperty(asset)) {
            const fullName = `${asset}-${network}`;
            if (Array.isArray(assets[asset]?.data)) {
              result[fullName] = assets[asset]?.data;
              contract[fullName] = assets[asset]?.contract || null;
            } else {
              const today = moment().format('YYYY-MM-DD');
              const oldDate = moment()
                .subtract(dataDuration?.id, 'days')
                .format('YYYY-MM-DD');
              result[fullName] = [
                {
                  Date: today,
                  Holder_Count: 0,
                  timestamp: new Date(today).getTime()
                },
                {
                  Date: oldDate,
                  Holder_Count: 0,
                  timestamp: new Date(oldDate).getTime()
                },
              ];
              contract[fullName] = assets[asset]?.contract || null;
            }
          }
        }
      }
    }

    return { result, contract };
  }

  const getGrowthPercentage = (data: any[] = []) => {
    if (!data || data.length < 2) return 0;
    
    const sortedData = [...data].sort(
      (a: any, b: any) =>
        new Date(a.Date).getTime() - new Date(b.Date).getTime()
    );
    
    const firstValue = sortedData[0]?.Holder_Count;
    const lastValue = sortedData[data.length - 1]?.Holder_Count;

    // Handle zero values to avoid division by zero
    if (firstValue === 0) return lastValue > 0 ? 100 : 0;
    
    const rateOfChange = (
      ((lastValue - firstValue) / firstValue) *
      100
    ).toFixed(2);

    const rateOfChangeInt = parseFloat(rateOfChange);
    if (isNaN(rateOfChangeInt)) return 0;
    if (rateOfChangeInt > 0) return '+' + rateOfChangeInt;

    return rateOfChangeInt;
  };

  // Create function to prepare modal data with detailed info if available
  const getModalData = (item: string, chartData: any[]) => {
    // If this is the selected token and we have detailed data, use that
    if (item === selectedToken && detailedData?.result) {
      // Format the detailed data for the chart
      const formattedDetailedData = detailedData.result.map((dataPoint: any) => ({
        Date: dataPoint.timestamp.split('T')[0],
        Holder_Count: dataPoint.totalHolders,
        timestamp: new Date(dataPoint.timestamp).getTime(),
        netHolderChange: dataPoint.netHolderChange,
        holderPercentChange: dataPoint.holderPercentChange,
        newHoldersByAcquisition: dataPoint.newHoldersByAcquisition
      }));
      
      // Calculate trend data
      const sortedData = [...formattedDetailedData].sort(
        (a, b) => new Date(a.Date).getTime() - new Date(b.Date).getTime()
      );
      
      const firstValue = sortedData[0]?.Holder_Count || 0;
      const lastValue = sortedData[sortedData.length - 1]?.Holder_Count || 0;
      
      const rateOfChange = firstValue === 0 ? 0 : ((lastValue - firstValue) / firstValue) * 100;
      
      return {
        data: formattedDetailedData,
        trendData: {
          isPositive: rateOfChange >= 0,
          rateOfChange: rateOfChange,
          firstValue: firstValue,
          lastValue: lastValue
        }
      };
    }
    
    // Otherwise use the regular chart data
    return {
      data: chartData,
      trendData: {
        isPositive: parseFloat(getGrowthPercentage(chartData)) > 0,
        rateOfChange: parseFloat(getGrowthPercentage(chartData)),
        firstValue: chartData[0]?.Holder_Count || 0,
        lastValue: chartData[chartData.length - 1]?.Holder_Count || 0
      }
    };
  };

  const primaryAxis = React.useMemo(
    (): AxisOptions<MyDatum> => ({
      getValue: (datum) => datum.Date,
      showGrid: false,
      showDatumElements: false,
      tickCount: 0,
      show: false,
    }),
    []
  );

  const getDataDomain = (data: any[] = []) => {
    if (!data || data.length === 0) return [0, 100];
    
    const values = data?.map((d) => d.Holder_Count);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    return [minValue - 150, maxValue + 150];
  };

  // Handler for item click - delegates to parent component
  const handleItemClick = (index: number, item: string) => {
    const [token, chain] = item.split('-');
    if (token && chain && dataToUse?.[chain]?.[token]) {
      // Set loading state when clicking - this will persist until data arrives
      setIsLoading(true);
      const tokenData = dataToUse[chain][token];
      // Pass contract, chain and token to parent component
      onSelectionChange(index, tokenData.contract, chain, token);
    }
  };

  return (
    <div id="historical-holder-count" className={styles.container}>
      <div className={styles.chartHeader}>
        <div>Historical Holder Count</div>
        {paginatedData?.length > 0 && (
          <div className={styles.paginationWrapper}>
            <TablePagination
              rowsPerPage={rowsPerPage}
              page={page}
              dataLength={Object.keys(holderData)?.length}
              setPage={setPage}
              classes={{
                iconButton: styles.paginationIconButton,
                icon: styles.paginationIcon,
                select: styles.paginationSelect,
                selectIcon: styles.paginationSelectIcon
              }}
            />
          </div>
        )}
      </div>

      {loading?.historicHolder ? (
        <div className={styles.contentWrapper}>
          <Spinner />
        </div>
      ) : paginatedData?.length === 0 ? (
        <div className={styles.contentWrapper}>
          <div className={styles.emptyState}>
            <div className={styles.emptyStateIcon}>
              <Activity size={48} opacity={0.5} />
            </div>
            <h3 className={styles.emptyStateTitle}>No Holder Data</h3>
            <p className={styles.emptyStateDescription}>
              Historical holder count unavailable for tracked assets.
            </p>
          </div>
        </div>
      ) : (
        <div className={styles.contentWrapper}>
          {paginatedData?.map((item: any, index: number) => {
            const chartData = holderData?.[item] ? [...holderData?.[item]] : [];
            return (
              chartData &&
              chartData?.length > 0 && (
                <div
                  key={item}
                  onClick={() => handleItemClick(index, item)}
                  className={cn(styles.row, {
                    [styles.selected]: index === selectedIndex,
                  })}
                >
                  <div
                    className={cn(styles.chain, {
                      [styles.chainSelected]: index === selectedIndex,
                    })}
                  >
                    <div>{item?.split('-')[0]}</div>
                    <div
                      className={cn({
                        [styles.chainValue]: true,
                        [styles.chainValueSelected]: index === selectedIndex,
                      })}
                    >
                      {index === selectedIndex ? (
                        isLoading ? (
                          // Show loading for net holder change
                          <span className={styles.loadingValue}>...</span>
                        ) : detailedData?.result ? (
                          // For selected token with Moralis data - show daily change
                          <span style={{ 
                            color: (detailedData.result[0]?.netHolderChange || 0) >= 0 ? '#47ff9c' : '#ff4747'
                          }}>
                            {(detailedData.result[0]?.netHolderChange || 0) > 0 ? '+' : ''}
                            {detailedData.result[0]?.netHolderChange || 0}
                          </span>
                        ) : null
                      ) : null}
                    </div>
                  </div>
                  <div className={styles.chartWrapper}>
                    <Chart
                      options={{
                        data: [
                          {
                            label: 'Holder Count',
                            data: chartData?.sort((a, b) => new Date(a.Date).getTime() - new Date(b.Date).getTime()) || [],
                          },
                        ],
                        primaryAxis,
                        secondaryAxes: [
                          {
                            getValue: (datum) => datum.Holder_Count,
                            elementType: 'line',
                            scaleType: 'linear',
                            hardMin: getDataDomain(holderData[item])[0],
                            hardMax: getDataDomain(holderData[item])[1],
                            showGrid: false,
                            tickCount: 0,
                            show: false,
                          },
                        ],
                        dark: true,
                        getDatumStyle: (datum, status) => ({
                          opacity: 1,
                          circle: { r: 5 },
                          rectangle: { stroke: 'black', strokeWidth: 3 },
                        }),
                        getSeriesStyle: (series) => ({
                          color: selectedIndex == index ? '#73F6FF' : 'white',
                          opacity: 1,
                        }),
                      }}
                    />
                  </div>
                  <div className={styles.value}>
                    {index === selectedIndex ? (
                      isLoading ? (
                        // Show loading indicator
                        <div><span className={styles.loadingValue}>Loading...</span></div>
                      ) : detailedData?.result ? (
                        // Show Moralis data when available
                        <div>
                          {new Intl.NumberFormat('en-US', {
                            notation: 'compact',
                            maximumFractionDigits: 1
                          }).format(detailedData.result[0]?.totalHolders || 0)}
                        </div>
                      ) : (
                        // Fallback to standard data
                        <div>
                          {new Intl.NumberFormat('en-US', {
                            notation: 'compact',
                            maximumFractionDigits: 1
                          }).format(chartData[chartData.length - 1]?.Holder_Count || 0)}
                        </div>
                      )
                    ) : null}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const chartData = holderData[item] || [];
                      // Use the new function to get enriched modal data
                      setModalData(getModalData(item, chartData));
                    }}
                    className={styles.expandButton}
                  >
                    <ExpandMoreIcon />
                  </button>
                </div>
              )
            );
          })}
        </div>
      )}

      <HolderCountChart
        open={modalData !== null}
        onClose={() => setModalData(null)}
        data={modalData?.data || []}
        trendData={modalData?.trendData}
      />
    </div>
  );
});

export default HistoricalHolderCount;















